import apiFetch from "@wordpress/api-fetch";
import { __ } from "@wordpress/i18n";
import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import PageHeading from "../../components/PageHeading/PageHeading";
import useAdminMenuHighlight from "../../hooks/useAdminMenuHighlight";
import "./Logs.scss";

const Logs = () => {
	useAdminMenuHighlight();
	const [selectedRows, setSelectedRows] = useState([]);
	const [bulkAction, setBulkAction] = useState("-1");
	const [logs, setLogs] = useState([]);
	const [filteredLogs, setFilteredLogs] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");

	const columns = [
		{
			id: "username",
			name: __("Username", "ultimate-security"),
			cell: (row) => (
				<div className="d-flex flex-wrap align-items-center gap-2">
					<span>
						<b>{row.display_name ? row.display_name : 'Unknown'}</b>
					</span>
				</div>
			),
		},
		{
			id: "login",
			name: __("Timestamp", "ultimate-security"),
			selector: (row) => timeAgo(row.created_at),
			sortable: true,
		},
		{
			id: "login-activity",
			name: __("Details", "ultimate-security"),
			cell: (row) => (
				<div className="login-activity-wrap">
					<span className="formated-time">{row.message}</span>
				</div>
			),
			ignoreRowClick: true,
			allowoverflow: true,
			minwidth: "250px",
			style: {
				display: "flex",
				justifyContent: "start",
				paddingRight: "11px",
			},
		},
	];

	useEffect(() => {
		fetchHistories("all");
	}, []);

	// Filter logs whenever logs or searchQuery changes
	useEffect(() => {
		if (searchQuery.trim() === "") {
			setFilteredLogs(logs);
		} else {
			const newData = logs.filter((row) => {
				return (
					(row.display_name &&
						row.display_name
							.toLowerCase()
							.includes(searchQuery.toLowerCase())) ||
					(row.message &&
						row.message
							.toLowerCase()
							.includes(searchQuery.toLowerCase())) ||
					(row.type &&
						row.type
							.toLowerCase()
							.includes(searchQuery.toLowerCase()))
				);
			});
			setFilteredLogs(newData);
		}
	}, [logs, searchQuery]);

	const fetchHistories = async (limit = null, offset = 0) => {
		try {
			let path = `/ultimate-security/v1/get-histories`;
			const params = [];
			if (limit !== null) params.push(`limit=${limit}`);
			if (offset > 0) params.push(`offset=${offset}`);
			if (params.length) path += `?${params.join("&")}`;

			const result = await apiFetch({ path });
			setLogs(result);
		} catch (err) {
			console.error("Error fetching histories:", err);
		}
	};

	function timeAgo(inputDate) {
		// Parse the input date as UTC
		const pastDate = new Date(inputDate + " UTC");
		const now = new Date();

		const nowUTC = Date.UTC(
			now.getUTCFullYear(),
			now.getUTCMonth(),
			now.getUTCDate(),
			now.getUTCHours(),
			now.getUTCMinutes(),
			now.getUTCSeconds(),
		);
		const pastDateUTC = Date.UTC(
			pastDate.getUTCFullYear(),
			pastDate.getUTCMonth(),
			pastDate.getUTCDate(),
			pastDate.getUTCHours(),
			pastDate.getUTCMinutes(),
			pastDate.getUTCSeconds(),
		);

		const diffInSeconds = Math.floor((nowUTC - pastDateUTC) / 1000);

		const units = [
			{ label: "year", seconds: 60 * 60 * 24 * 365 },
			{ label: "month", seconds: 60 * 60 * 24 * 30 },
			{ label: "week", seconds: 60 * 60 * 24 * 7 },
			{ label: "day", seconds: 60 * 60 * 24 },
			{ label: "hour", seconds: 60 * 60 },
			{ label: "minute", seconds: 60 },
			{ label: "second", seconds: 1 },
		];

		for (const unit of units) {
			const count = Math.floor(diffInSeconds / unit.seconds);
			if (count > 0) {
				return `${count} ${unit.label}${count > 1 ? "s" : ""} ago`;
			}
		}

		return "Just now";
	}

	const handleSelectedRowsChange = ({ selectedRows }) => {
		setSelectedRows(selectedRows);
	};

	const handleUserSearch = (e) => {
		const query = e.target.value;
		setSearchQuery(query);
	};

	const handleBulkActionChange = (e) => {
		setBulkAction(e.target.value);
	};

	const handleBulkActionSubmit = async () => {
		if (selectedRows.length === 0) {
			alert("Please select logs.");
			return;
		}

		if (bulkAction !== "delete") {
			alert("Please select a valid action.");
			return;
		}

		const logIds = selectedRows.map((item) => item.id);

		try {
			// Use Promise.all for better performance and error handling
			const deletePromises = logIds.map((logId) =>
				apiFetch({
					path: "/ultimatesecurity/v1/delete-logs",
					method: "POST",
					data: { log_id: parseInt(logId) },
				}),
			);

			await Promise.all(deletePromises);

			// Refresh the logs after deletion
			await fetchHistories();
			setSelectedRows([]);
			setBulkAction("-1");
			alert("Selected logs deleted successfully.");
		} catch (error) {
			console.error("Error during bulk log deletion:", error);
			alert("One or more deletions failed. Please try again.");
		}
	};

	return (
		<>
			<div className="ultimate-security-logs-wrapper">
				<div className="container-fluid">
					<div className="form-header pb-lg mb-lg border-bottom">
						<PageHeading
							title={__("Logs", "ultimate-security")}
							subtitle={__(
								"View and manage security logs for your website.",
								"ultimate-security",
							)}
						/>
					</div>
					<div className="setting-unit">
						<div className="user-datatable datatable-unit">
							<div className="row justify-content-between mb-30">
								<div className="col-8 filters">
									<div className="bulk-action-wrapper">
										<select
											value={bulkAction}
											name="action"
											onChange={handleBulkActionChange}
										>
											<option value="-1">
												{__(
													"Bulk actions",
													"ultimate-security",
												)}
											</option>
											<option value="delete">
												{__(
													"Delete",
													"ultimate-security",
												)}
											</option>
										</select>
										<button
											className="button button-secondary"
											onClick={handleBulkActionSubmit}
										>
											{__("Apply", "ultimate-security")}
										</button>
									</div>
								</div>
								<div className="col-auto">
									<div className="search-unit d-flex gap-2 align-items-center">
										<input
											placeholder="Search logs ..."
											type="search"
											onChange={handleUserSearch}
											value={searchQuery}
										/>
									</div>
								</div>
							</div>
							<div className="table-wrapper">
								<DataTable
									columns={columns}
									data={filteredLogs} // Use filtered data
									selectableRows
									onSelectedRowsChange={
										handleSelectedRowsChange
									}
									pagination
									paginationPerPage={15} // Default to 5 rows per page
									paginationRowsPerPageOptions={[
										15, 25, 50,
									]} // Options with 5 first
									dense
									responsive
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};
export default Logs;
